import { useRef, useMemo, useState, useEffect } from 'react';
import qs from 'qs';

const useParams = () => {
  const updateQs = useRef(false);
  const { query, page, sort, currentContent, ...filters } = useMemo(() => {
    const {
      query,
      page,
      sort,
      currentContent,
      ...filters
    } = qs.parse(location.search, { ignoreQueryPrefix: true });

    Object.keys(filters).forEach(filter => {
      if (typeof filters[filter] === 'string') {
        filters[filter] = [filters[filter]];
      }
    });

    filters.dates = ['Upcoming'];

    return { query, page, sort, currentContent, ...filters };
  }, []);

  const [params, setParams] = useState({
    query: query || '',
    page: page || 1,
    sort,
    filters,
    currentContent
  });

  useEffect(() => {
    const buildQueryString = () => {
      const qsParams = {};

      if (params.query) {
        qsParams.query = params.query;
      }

      if (params.page && params.page > 1) {
        qsParams.page = params.page;
      }

      if (params.sort) {
        qsParams.sort = params.sort;
      }

      if (params.filters) {
        Object.keys(params.filters).forEach(key => {
          qsParams[key] = params.filters[key];
        });
      }

      if (params.currentContent) {
        qsParams.currentContent = params.currentContent;
      }

      const qsValue = qs.stringify(qsParams, {
        encode: false,
        encodeValuesOnly: true,
        arrayFormat: 'repeat'
      });

      const newUrl = `${location.protocol}//${location.host}${
        location.pathname
      }${qsValue ? `?${qsValue}` : ''}`;
      history.pushState({}, '', newUrl);
    };

    if (updateQs.current) {
      buildQueryString();
    } else {
      updateQs.current = true;
    }
  }, [params]);

  useEffect(() => {
    const handlePopState = () => {
      const qsParams = qs.parse(location.search, { ignoreQueryPrefix: true });
      updateQs.current = false;

      setParams({
        query: qsParams.query || '',
        page: qsParams.page || 1,
        sort: qsParams.sort,
        filters: qsParams.filters,
        currentContent: qsParams.currentContent
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setQuery = query => {
    setParams({ ...params, query, page: 1 });
  };

  const setPage = page => {
    setParams({ ...params, page });
  };

  const setSort = sort => {
    setParams({ ...params, sort, page: 1 });
  };

  const setFilters = filters => {
    setParams({ ...params, filters });
  };

  const setCurrentContent = currentContent => {
    setParams({ ...params, currentContent });
  };

  return [params, setQuery, setPage, setSort, setFilters, setCurrentContent];
};

export default useParams;
